/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import { DeliveryNoteStatusEnum } from 'commons/enums';
import {
  DashboardState,
  IBin,
  IBinCommodity,
  IDeliveryNote,
  Order,
  ReplenishTask,
  User,
  IStockTransfer,
  DashboardActiveOrderSummaries,
  DashboardShiftSummaries,
  DashboardSetHighPriority,
  IFileImport,
  DashboardSetHighPriorityReplenish,
  ProductDetail,
  InventoryAdjustment,
  STODocument,
  STOItem,
  ICompanyInfo,
  IInboundTaskDocument,
  IInboundTaskItem,
  IInboundInvoice,
} from 'commons/types';
import { ReducerKeys } from 'redux-stores/enums/reducerKey.enum';

const initialState: DashboardState = {
  orderList: [],
  replenishList: [],
  summaries: {
    activeOrderSummaries: {
      orderCounts: {
        regular: {
          in_queue: 0,
          in_progress: 0,
        },
        alkes: {
          in_queue: 0,
          in_progress: 0,
        },
        precursor: {
          in_queue: 0,
          in_progress: 0,
        },
        ccp: {
          in_queue: 0,
          in_progress: 0,
        },
        psikotropika: {
          in_queue: 0,
          in_progress: 0,
        },
        oot: {
          in_queue: 0,
          in_progress: 0,
        },
      },
      currentActiveUserCount: 0,
    },
    shiftSummaries: {
      orderPerShifts: [],
      topUsers: [],
    },
  },
  userList: [],
  binList: [],
  binSkuList: [],
  binCommodityList: [],
  enabledBinCommodityList: [],
  stoDocumentList: [],
  stoItemList: { sto_number: null, list: [] },
  stockTransferList: [],
  inboundTaskDocumentList: [],
  inboundInvoiceList: [],
  inboundTaskItemList: { inbound_task_number: null, list: [] },
  inboundInvoiceItemList: {
    inbound_task_number: null,
    invoice_number: null,
    list: [],
  },
  activeLocations: [],
  selectedLocation: null,
  bulkUploadHistory: [],
  updateListPage: false,
  inventoryAdjustmentList: [],
  failedOrders: 0,
  deliveryNoteList: [],
};

const dashboardSlice = createSlice({
  initialState,
  name: ReducerKeys.Dashboard,
  reducers: {
    setAllOrderLists(
      state: DashboardState,
      action: { type: string; payload: Order[] },
    ) {
      state.orderList = action.payload;
    },
    setAllReplenishLists(
      state: DashboardState,
      action: { type: string; payload: ReplenishTask[] },
    ) {
      state.replenishList = action.payload;
    },
    setAllUserLists(
      state: DashboardState,
      action: { type: string; payload: User[] },
    ) {
      state.userList = action.payload;
    },
    setAllBinLists(
      state: DashboardState,
      action: { type: string; payload: IBin[] },
    ) {
      state.binList = action.payload;
    },
    addBinToList(
      state: DashboardState,
      action: { type: string; payload: IBin },
    ) {
      state.binList = [action.payload, ...state.binList];
    },
    setAllBinSkuLists(
      state: DashboardState,
      action: { type: string; payload: ProductDetail[] },
    ) {
      state.binSkuList = action.payload;
    },
    setBinCommodityList(
      state: DashboardState,
      action: { type: string; payload: IBinCommodity[] },
    ) {
      state.binCommodityList = action.payload;
    },
    setEnabledBinCommodityList(
      state: DashboardState,
      action: { type: string; payload: IBinCommodity[] },
    ) {
      state.enabledBinCommodityList = action.payload;
    },
    setBinType(
      state: DashboardState,
      action: { type: string; payload: { bin: string; bin_type: string } },
    ) {
      const { bin, bin_type: binType } = action.payload;
      state.binSkuList = state.binSkuList.map((binData) => {
        return {
          ...binData,
          bin_type: binData.bin === bin ? binType : binData.bin_type,
        };
      });
      state.binList = state.binList.map((binData) => {
        return {
          ...binData,
          bin_type: binData.name === bin ? binType : binData.bin_type,
        };
      });
    },
    setBinCommodity(
      state: DashboardState,
      action: {
        type: string;
        payload: { bin: string; commodity: string | null };
      },
    ) {
      const { bin, commodity } = action.payload;
      state.binSkuList = state.binSkuList.map((binData) => {
        return {
          ...binData,
          commodity: binData.bin === bin ? commodity : binData.commodity,
        };
      });
      state.binList = state.binList.map((binData) => {
        return {
          ...binData,
          commodity: binData.name === bin ? commodity : binData.commodity,
        };
      });
    },
    setAllStockTransferLists(
      state: DashboardState,
      action: { type: string; payload: IStockTransfer[] },
    ) {
      state.stockTransferList = action.payload;
    },
    setAllInventoryAdjustmentLists(
      state: DashboardState,
      action: { type: string; payload: InventoryAdjustment[] },
    ) {
      state.inventoryAdjustmentList = action.payload;
    },
    setAllSTODocumentList(
      state: DashboardState,
      action: { type: string; payload: STODocument[] },
    ) {
      state.stoDocumentList = action.payload;
    },
    setAllSTOItemList(
      state: DashboardState,
      action: {
        type: string;
        payload: { sto_number: string; list: STOItem[] };
      },
    ) {
      state.stoItemList = action.payload;
    },
    setAllInboundTaskDocumentList(
      state: DashboardState,
      action: { type: string; payload: IInboundTaskDocument[] },
    ) {
      state.inboundTaskDocumentList = action.payload;
    },
    setAllInboundInvoiceList(
      state: DashboardState,
      action: { type: string; payload: IInboundInvoice[] },
    ) {
      state.inboundInvoiceList = action.payload;
    },
    setAllInboundTaskItemList(
      state: DashboardState,
      action: {
        type: string;
        payload: { inbound_task_number: string; list: IInboundTaskItem[] };
      },
    ) {
      state.inboundTaskItemList = action.payload;
    },
    setAllInboundInvoiceItemList(
      state: DashboardState,
      action: {
        type: string;
        payload: {
          inbound_task_number: string;
          invoice_number: string;
          list: IInboundTaskItem[];
        };
      },
    ) {
      state.inboundInvoiceItemList = action.payload;
    },
    setSummariesActiveOrder(
      state: DashboardState,
      action: { type: string; payload: DashboardActiveOrderSummaries },
    ) {
      state.summaries.activeOrderSummaries = action.payload;
    },
    setSummariesShifts(
      state: DashboardState,
      action: { type: string; payload: DashboardShiftSummaries },
    ) {
      state.summaries.shiftSummaries = action.payload;
    },
    setHighPriorityValue(
      state: DashboardState,
      action: { type: string; payload: DashboardSetHighPriority },
    ) {
      const { payload } = action;
      const newOrderList = state.orderList.map((order) => {
        if (payload.po_numbers.includes(order.po_number))
          return { ...order, is_high_priority: payload.value };

        return order;
      });
      state.orderList = newOrderList;
    },
    setSelectedLocation(
      state: DashboardState,
      action: { type: string; payload: ICompanyInfo },
    ) {
      state.selectedLocation = action.payload;
    },
    setActiveLocations(
      state: DashboardState,
      action: { type: string; payload: ICompanyInfo[] },
    ) {
      state.activeLocations = action.payload;
    },
    setBulkUploadHistory(
      state: DashboardState,
      action: { type: string; payload: IFileImport[] },
    ) {
      state.bulkUploadHistory = action.payload;
    },
    setUpdateListPage(state: DashboardState) {
      state.updateListPage = !state.updateListPage;
    },
    setHighPriorityValueReplenish(
      state: DashboardState,
      action: {
        type: string;
        payload: DashboardSetHighPriorityReplenish;
      },
    ) {
      const { payload } = action;
      const newReplenishList = state.replenishList.map((replenish) => {
        if (payload.replenish_numbers.includes(replenish.replenish_number))
          return { ...replenish, is_high_priority: payload.value };
        return replenish;
      });
      state.replenishList = newReplenishList;
    },
    setFailedOrders(
      state: DashboardState,
      action: {
        type: string;
        payload: {
          failedOrders: number;
        };
      },
    ) {
      const { payload } = action;
      state.failedOrders = payload.failedOrders;
    },
    removeOrderFromList(
      state: DashboardState,
      action: {
        type: string;
        payload: { po_numbers: string[] };
      },
    ) {
      const { po_numbers: poNumbers } = action.payload;
      const newOrderList = state.orderList.filter(
        (order) => !poNumbers.includes(order.po_number),
      );
      state.orderList = newOrderList;
    },
    setAllDeliveryNoteLists(
      state: DashboardState,
      action: { type: string; payload: IDeliveryNote[] },
    ) {
      state.deliveryNoteList = action.payload;
    },
    setDeliveryNoteStatus(
      state: DashboardState,
      action: {
        type: string;
        payload: { awbNumber: string; status: DeliveryNoteStatusEnum };
      },
    ) {
      const { awbNumber, status } = action.payload;
      state.deliveryNoteList = state.deliveryNoteList.map((deliveryNote) =>
        deliveryNote.awb_number === awbNumber
          ? { ...deliveryNote, status }
          : deliveryNote,
      );
    },
  },
});

export const {
  setAllOrderLists,
  setAllReplenishLists,
  setAllUserLists,
  setAllBinLists,
  addBinToList,
  setAllBinSkuLists,
  setBinCommodityList,
  setEnabledBinCommodityList,
  setBinType,
  setBinCommodity,
  setAllStockTransferLists,
  setAllInventoryAdjustmentLists,
  setAllSTODocumentList,
  setAllSTOItemList,
  setAllInboundTaskDocumentList,
  setAllInboundInvoiceList,
  setAllInboundTaskItemList,
  setAllInboundInvoiceItemList,
  setSummariesActiveOrder,
  setSummariesShifts,
  setHighPriorityValue,
  setSelectedLocation,
  setActiveLocations,
  setBulkUploadHistory,
  setUpdateListPage,
  setHighPriorityValueReplenish,
  setFailedOrders,
  removeOrderFromList,
  setAllDeliveryNoteLists,
  setDeliveryNoteStatus,
} = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;
